import { Icon, Label } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, MouseEvent, useRef } from 'react';
import { TEMPLATE_CATEGORY_DATA_ATTRIBUTE_KEY } from '../../constant';
import { useTemplateContext } from '../../context';
import { CategoryType } from '../../types';
import { ExportTemplateItemBadge } from '../export-template-item-badge';
import { TemplateEntries } from '../template-entries';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateCategoryProps as Props } from './types';

const ACCORDION_SLOT_PROPS = { transition: { timeout: 0 } };

export const TemplateCategory = memo(
  ({
    className,
    category,
    checkedValues,
    categoryWithNoChildren: _categoryWithNoChildren,
    expanded,
    onToggle,
    onSelectAll,
    onDeselectAll,
    onToggleEntryChecked,
    onActiveEntry,
    selectedOptionsCount,
    onAddEntry,
    onRemove,
    selectedOptionsCountInEntry,
    onClear,
    onToggleExpanded,
  }: Props) => {
    const { isLoading } = useTemplateContext();
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const isCompany = category.type === CategoryType.COMPANY;
    const iconColor = isCompany
      ? theme.palette.primary[500]
      : theme.palette.success[700];

    const handleClear = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (selectedOptionsCount) onClear();
    };

    const handleExpand = (
      e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
    ) => {
      if (ref && ref.current && (e.target as HTMLElement).getAttribute('rel')) {
        return;
      }
      onToggleExpanded();
    };

    return (
      <Styled.Wrapper
        className={classnames('TemplateCategory', className)}
        sx={SX.root}
        expanded={expanded}
        slotProps={ACCORDION_SLOT_PROPS}
        {...{ [TEMPLATE_CATEGORY_DATA_ATTRIBUTE_KEY]: category.name }}
      >
        <Styled.Summary ref={ref} onClick={(e) => handleExpand(e)}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={SX.summaryContent}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Icon
                  name={
                    !expanded
                      ? 'arrow-right-outline'
                      : 'arrow-dropdown-down-outline'
                  }
                  color={iconColor}
                  size={18}
                />
                <Icon
                  name={isCompany ? 'business' : 'people'}
                  sxx={SX.typeIcon(isCompany)}
                  size={20}
                />
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body3" fontWeight={500} color="grey.700">
                  {category.name}
                </Typography>

                {category.hasInfoBadge && (
                  <ExportTemplateItemBadge checkedValues={checkedValues} />
                )}
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box display={'inline-flex'} rel="clear" onClick={handleClear}>
                <Button
                  variant="text"
                  color="default"
                  size="xsmall"
                  disabled={!selectedOptionsCount}
                >
                  Clear
                </Button>
              </Box>
              {isLoading ? (
                <Icon
                  name="loading-twotone-loop"
                  size={18}
                  color="primary.300"
                />
              ) : (
                <Label color="primary" variant="soft" size="xxsmall">
                  {selectedOptionsCount}
                </Label>
              )}
            </Stack>
          </Stack>
        </Styled.Summary>
        <Styled.Detail>
          <TemplateEntries
            entries={category.entries}
            onToggle={onToggle}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
            onToggleEntryChecked={onToggleEntryChecked}
            onActiveEntry={onActiveEntry}
            onAddEntry={onAddEntry}
            onRemove={onRemove}
            selectedOptionsCountInEntry={selectedOptionsCountInEntry}
            isSingle={category.isSingleEntry}
            checkedValues={checkedValues}
          />
        </Styled.Detail>
      </Styled.Wrapper>
    );
  }
);

TemplateCategory.displayName = 'TemplateCategory';
